declare const require: (module: string) => any;

import { Injectable, isDevMode } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { HubConnection } from '@microsoft/signalr';
import { IProduct } from '../models/products/product';
import { IAsset } from '../models/products/asset';
import {
  SignalrNotification,
  SignalrComment,
  SignalrAsset,
  SignalrProductSearch,
  SignalrProduct,
  SignalrShootAsset,
  SignalrPostAsset,
  SignalrBatch,
  SignalrPackage,
  SignalrSetting,
  SignalrMasterSuite,
  SignalrUser,
  SignalrOrganization,
} from '../models/signalr.model';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  connection?: HubConnection;
  private connect$ = new BehaviorSubject<boolean>(false);
  public connectState = this.connect$.asObservable();
  public connectionId = '';

  private notification$ = new BehaviorSubject<SignalrNotification>({} as SignalrNotification);
  public notificationData = this.notification$.asObservable();

  private chatBox$ = new BehaviorSubject<SignalrComment>({} as SignalrComment);
  public chatBoxData = this.chatBox$.asObservable();

  private asset$ = new BehaviorSubject<SignalrAsset>({} as SignalrAsset);
  public assetData = this.asset$.asObservable();

  private productsShot$ = new BehaviorSubject<SignalrProductSearch>({} as SignalrProductSearch);
  public productsShotData = this.productsShot$.asObservable();

  private productsEnhanced$ = new BehaviorSubject<SignalrProductSearch>({} as SignalrProductSearch);
  public productsEnhancedData = this.productsEnhanced$.asObservable();

  private productsReviewed$ = new BehaviorSubject<SignalrProductSearch>({} as SignalrProductSearch);
  public productsReviewedData = this.productsReviewed$.asObservable();

  private products$ = new BehaviorSubject<SignalrProduct>({} as SignalrProduct);
  public productsData = this.products$.asObservable();

  private shootAssets$ = new BehaviorSubject<SignalrShootAsset>({} as SignalrShootAsset);
  public shootAssetsData = this.shootAssets$.asObservable();

  private postAssets$ = new BehaviorSubject<SignalrPostAsset>({} as SignalrPostAsset);
  public postAssetsData = this.postAssets$.asObservable();

  private batches$ = new BehaviorSubject<SignalrBatch>({} as SignalrBatch);
  public batchesData = this.batches$.asObservable();

  private packages$ = new BehaviorSubject<SignalrPackage>({} as SignalrPackage);
  public packagesData = this.packages$.asObservable();

  private settings$ = new BehaviorSubject<SignalrSetting>({} as SignalrSetting);
  public settingsData = this.settings$.asObservable();

  private masterSuites$ = new BehaviorSubject<SignalrMasterSuite>({} as SignalrMasterSuite);
  public masterSuitesData = this.masterSuites$.asObservable();

  private users$ = new BehaviorSubject<SignalrUser>({} as SignalrUser);
  public usersData = this.users$.asObservable();

  private organizations$ = new BehaviorSubject<SignalrOrganization>({} as SignalrOrganization);
  public organizationsData = this.organizations$.asObservable();

  constructor() {}

  clearNotification() {
    this.notification$.next({} as SignalrNotification);
  }

  clearChatBox() {
    this.chatBox$.next({} as SignalrComment);
  }

  clearAsset() {
    this.asset$.next({} as SignalrAsset);
  }

  clearProductsShot() {
    this.productsShot$.next({} as SignalrProductSearch);
  }

  clearProductsEnhanced() {
    this.productsEnhanced$.next({} as SignalrProductSearch);
  }

  clearProductsReviewed() {
    this.productsReviewed$.next({} as SignalrProductSearch);
  }

  clearProducts() {
    this.products$.next({} as SignalrProduct);
  }

  clearShootAssets() {
    this.shootAssets$.next({} as SignalrShootAsset);
  }

  clearPostAssets() {
    this.postAssets$.next({} as SignalrPostAsset);
  }

  clearBatches() {
    this.batches$.next({} as SignalrBatch);
  }

  clearPackages() {
    this.packages$.next({} as SignalrPackage);
  }

  clearSettings() {
    this.settings$.next({} as SignalrSetting);
  }

  clearMasterSuites() {
    this.masterSuites$.next({} as SignalrMasterSuite);
  }

  clearUsers() {
    this.users$.next({} as SignalrUser);
  }

  clearOrganizations() {
    this.organizations$.next({} as SignalrOrganization);
  }


  joinStaticGroup(name: string): Promise<string> | undefined {
    try {
      if (!this.connect$.value) {
        if (isDevMode()) {
          console.log('[DEV] [SignalrService] not connected yet, skipping joinStaticGroup', name);
        }
        return undefined;
      }
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] joining static group', name);
      }
      return this.connection!.invoke(name).then((group: string) => {
        return group;
      });
    } catch (error) {
      console.error('[SignalrService] joinStaticGroup failed', error);
      return undefined;
    }
  }

  joinIdGroup(name: string, id: string): Promise<string> | undefined {
    try {
      if (!this.connect$.value) {
        if (isDevMode()) {
          console.log('[DEV] [SignalrService] not connected yet, skipping joinIdGroup', name);
        }
        return undefined;
      }
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] joining id group', name, id);
      }
      return this.connection!.invoke(name, id).then((group: string) => {
        return group;
      });
    } catch (error) {
      console.error('[SignalrService] joinIdGroup failed', error);
      return undefined;
    }
  }

  joinChannelGroup(name: string, id: string, channel: string): Promise<string> | undefined {
    try {
      if (!this.connect$.value) {
        if (isDevMode()) {
          console.log('[DEV] [SignalrService] not connected yet, skipping joinChannelGroup', name);
        }
        return undefined;
      }
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] joining channel group', name, id, channel);
      }
      return this.connection!.invoke(name, id, channel).then((group: string) => group);
    } catch (error) {
      console.error('[SignalrService] joinChannelGroup failed', error);
      return undefined;
    }
  }

  leaveGroup(group: string) {
    try {
      if (!this.connect$.value) {
        if (isDevMode()) {
          console.log('[DEV] [SignalrService] not connected yet, skipping leaveGroup', group);
        }
        return;
      }
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] leaving group', group);
      }
      this.connection!.invoke('LeaveGroup', group).then((data: unknown) => {});
    } catch (error) {
      console.error('[SignalrService] leaveGroup failed', error);
    }
  }

  connect(accessToken: string) {
    if (this.connection) return;
    const signalR = require('@microsoft/signalr');
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBase}/messageHub`, {
        accessTokenFactory: () => accessToken,
      })
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    this.connection!.start().then(
      () => {
        if (isDevMode()) {
          console.log('[DEV] [SignalrService] connected');
        }
        this.connectionId = (this.connection as any).connection.connectionId;
        this.connect$.next(true);
      },
      (error: unknown) => {
        console.error('[SignalrService] connection failed', error);
        this.connect$.next(false);
      },
    );

    this.connection!.on('onConnected', (data: unknown) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onConnected', data);
      }
    });
    this.connection!.on('onReceivedNotification', (data: SignalrNotification) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedNotification', data);
      }
      this.notification$.next(data);
    });

    // DashBoard
    this.connection!.on('onReceivedProductsShot', (data: SignalrProductSearch) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedProductsShot', data);
      }
      this.productsShot$.next(data);
    });
    this.connection!.on('onReceivedProductsEnhanced', (data: SignalrProductSearch) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedProductsEnhanced', data);
      }
      this.productsEnhanced$.next(data);
    });
    this.connection!.on('onReceivedProductsReviewed', (data: SignalrProductSearch) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedProductsReviewed', data);
      }
      this.productsReviewed$.next(data);
    });

    // Product Changes
    this.connection!.on('onReceivedProduct', (data: IProduct) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedProduct', data);
      }
      this.products$.next(data);
    });
    // Product Changes
    this.connection!.on('onReceivedProducts', (data: { products: IProduct[] }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedProducts', data);
      }
      data.products.forEach((product) => {
        this.products$.next(product);
      });
    });

    this.connection!.on('onReceivedShootAsset', (data: IAsset) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedShootAsset', data);
      }
      this.shootAssets$.next(data);
    });

    this.connection!.on('onReceivedPostAsset', (data: IAsset) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPostAsset', data);
      }
      this.postAssets$.next(data);
    });

    // Batch Changes
    this.connection!.on('onReceivedBatch', (data: SignalrBatch) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedBatch', data);
      }
      this.batches$.next(data);
    });

    // Package Changes
    this.connection!.on('onReceivedPackage', (data: SignalrPackage) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPackage', data);
      }
      this.packages$.next(data);
    });

    // Settings Changes
    this.connection!.on('onReceivedSetting', (data: SignalrSetting) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedSetting', data);
      }
      this.settings$.next(data);
    });

    // Master Suites Changes
    this.connection!.on('onReceivedMasterSuite', (data: SignalrMasterSuite) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedMasterSuite', data);
      }
      this.masterSuites$.next(data);
    });

    // User Changes
    this.connection!.on('onReceivedUser', (data: SignalrUser) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedUser', data);
      }
      this.users$.next(data);
    });

    // Organizations Changes
    this.connection!.on('onReceivedOrganization', (data: SignalrOrganization) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedOrganization', data);
      }
      this.organizations$.next(data);
    });

    // Asset Review and Chat box
    this.connection!.on('onReceivedShootAssetComment', (data: { comment: SignalrComment }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedShootAssetComment', data);
      }
      this.chatBox$.next(data.comment);
    });
    this.connection!.on('onReceivedPostAssetExternalComment', (data: { comment: SignalrComment }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPostAssetExternalComment', data);
      }
      this.chatBox$.next(data.comment);
    });
    this.connection!.on('onReceivedPostAssetInternalComment', (data: { comment: SignalrComment }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPostAssetInternalComment', data);
      }
      this.chatBox$.next(data.comment);
    });

    this.connection!.on('onReceivedShootAssetReview', (data: { shootAsset: SignalrAsset }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedShootAssetReview', data);
      }
      this.asset$.next(data.shootAsset);
    });
    this.connection!.on('onReceivedPostAssetInternalReview', (data: { postAsset: SignalrAsset }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPostAssetInternalReview', data);
      }
      this.asset$.next(data.postAsset);
    });
    this.connection!.on('onReceivedPostAssetExternalReview', (data: { postAsset: SignalrAsset }) => {
      if (isDevMode()) {
        console.log('[DEV] [SignalrService] onReceivedPostAssetExternalReview', data);
      }
      this.asset$.next(data.postAsset);
    });
  }
}
